import { useReducer, useEffect, useState, useRef } from "react";
import DataGrid, {
  Column,
  RequiredRule,
  Paging,
  Pager,
  StringLengthRule,
} from "devextreme-react/data-grid";
import { find, get, unionBy, uniq, uniqBy } from "lodash";
import { Toast } from "devextreme-react";
import { alert } from "devextreme/ui/dialog";

import * as actions from "../../actions/settings";
import settingsReducer from "../../reducers/settings";

const initialState = {
  languagesByRegion: null,
  inputTypeReasonCodeByLangId: null,
  toastData: null,
};

const SixMConfiguration = () => {
  const [selectedLanguage, setSelectedLanguage] = useState();
  const [toastVisible, setToastVisible] = useState(false);
  const [state, dispatch] = useReducer(settingsReducer, initialState);
  const { languagesByRegion, inputTypeReasonCodeByLangId, toastData } = state;

  useEffect(() => {
    if (!languagesByRegion) {
      actions.fetchLanguagesByRegion(dispatch, "");
    }

    if (
      languagesByRegion &&
      Array.isArray(languagesByRegion) &&
      languagesByRegion.length > 0
    ) {
      setSelectedLanguage(languagesByRegion[0]?.languageID);
    }
  }, [languagesByRegion]);

  useEffect(() => {
    if (selectedLanguage) {
      actions.fetchInputTypeReasonCodeByLangID(dispatch, selectedLanguage);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (toastData) {
      setToastVisible(true);
    }
  }, [toastData]);

  const gridConfig = {
    showBorders: true,
    rowAlternationEnabled: true,
    allowColumnReordering: true,
    allowColumnResizing: true,
    columnResizingMode: "widget",
    columnAutoWidth: true,

    headerFilter: {
      visible: true,
    },
    sorting: {
      mode: "multiple",
    },
    grouping: {
      contextMenuEnabled: true,
    },
    searchPanel: {
      visible: true,
    },
    scrolling: { showScrollbar: "always" },
    filterRow: {
      visible: true,
      applyFilter: "auto",
    },

    editing: {
      mode: "batch",
      allowUpdating: true,
      useIcons: true,
    },
    onToolbarPreparing: function (e) {
      var dataGrid = e.component;
      var toolbarItems = e.toolbarOptions.items;
      toolbarItems.unshift(
        {
          location: "before",
          template: "6M Config",
        },
        {
          location: "after",
          widget: "dxSelectBox",
          options: {
            width: "200px",
            placeholder: "Select Language....",
            items: languagesByRegion
              ? uniqBy(languagesByRegion, "languageID")
              : [],
            valueExpr: "languageID",
            displayExpr: "language",
            onValueChanged: (e) => {
              setSelectedLanguage(e.value);
            },
            value: selectedLanguage,
          },
        },
        {
          location: "after",
          widget: "dxButton",
          options: {
            icon: "refresh",
            hint: "Refresh",
            onClick: function () {
              dataGrid.refresh();
            },
          },
        }
      );
    },
  };

  const handleSave = (e) => {
    const updatedData = e?.changes?.map((obj) => {
      return {
        inputTypeID: obj?.data?.inputTypeID,
        inputType: obj?.data?.languageTranslated,
      };
    });

    actions.Manage6MInputTypeData(dispatch, {
      languageID: selectedLanguage,
      InputTypeData: updatedData,
    });
  };

  const isAddedValueDuplicate = (data, newRow, keysToCheck) => {
    return data.some((entry) =>
      keysToCheck.every(
        (key) =>
          entry.hasOwnProperty(key) &&
          newRow.hasOwnProperty(key) &&
          String(entry[key]).toLowerCase() === String(newRow[key]).toLowerCase()
      )
    );
  };

  const hasDuplicateInGrid = (data, updatedData, keysToCheck) => {
    const combinedData = unionBy(updatedData, data, "inputTypeID");

    return keysToCheck.some((key) => {
      const values = combinedData.map((item) => {
        const value = item[key];
        return value == null ? `__null__${key}` : value.toLowerCase();
      });

      return uniq(values).length !== values.length;
    });
  };

  const hasEmptyRows = (data, keysToCheck) => {
    return data.some((item) =>
      keysToCheck.some((key) => typeof item[key] !== "string")
    );
  };

  const onRowValidating = (e) => {
    const updatedRow = { ...e.oldData, ...e.newData };

    // Check any of the row have null value. if its null then can't save.
    if (
      hasEmptyRows(
        unionBy(updatedRow, inputTypeReasonCodeByLangId, "inputTypeID"),
        ["languageTranslated"]
      )
    ) {
      return;
    }

    if (
      isAddedValueDuplicate(inputTypeReasonCodeByLangId, updatedRow, [
        "languageTranslated",
      ])
    ) {
      e.errorText = "Translated value Should not be same for 6M";
      e.isValid = false;
    }
  };

  const onSaveValidating = (e) => {
    const updatedRows = e?.changes?.map((change) => {
      return { ...change?.key, ...change.data };
    });

    // Check any row is empty. If empty showing message.
    if (
      hasEmptyRows(
        unionBy(updatedRows, inputTypeReasonCodeByLangId, "inputTypeID"),
        ["languageTranslated"]
      )
    ) {
      alert("Translated values required for all 6M to save data", "Alert");
      e.cancel = true;
      return;
    }
    // This Runs only all the rows are filled. then duplicate checking action performs
    if (
      hasDuplicateInGrid(inputTypeReasonCodeByLangId, updatedRows, [
        "languageTranslated",
      ])
    ) {
      alert("Translated Value Should not be same for 6M", "Alert");
      e.cancel = true;
    }
  };

  return (
    <div>
      <DataGrid
        dataSource={inputTypeReasonCodeByLangId}
        {...gridConfig}
        onSaved={handleSave}
        loadPanel={{
          enabled: true,
          showIndicator: true,
          text: "Loading",
          showPane: "true",
        }}
        onRowValidating={onRowValidating}
        onSaving={onSaveValidating}
      >
        <Paging defaultPageSize={10} />
        <Pager
          visible={true}
          allowedPageSizes={[10, 20, 50]}
          showPageSizeSelector={true}
          showInfo={true}
          showNavigationButtons={true}
        />

        <Column
          dataField="inputType"
          caption="6M Type"
          visible={true}
          allowEditing={false}
        ></Column>
        <Column
          dataField="languageTranslated"
          caption={`${get(
            find(
              languagesByRegion,
              (lang) => lang?.languageID === selectedLanguage
            ),
            "language",
            ""
          )} Translated Value`}
          visible={true}
        >
          <RequiredRule />
          <StringLengthRule
            max={30}
            message="Input Should not exceed more than 30 character length"
          />
        </Column>
      </DataGrid>
      <Toast
        visible={toastVisible}
        message={toastData?.message}
        type={toastData?.type}
        displayTime={2000}
        onHiding={() => setToastVisible(false)}
      />
    </div>
  );
};

export default SixMConfiguration;
